/**
 * RequireAuth.jsx — Route-level auth gate.
 *
 * Same check Account.jsx already performs on mount: probe
 * GET /api/auth/me with credentials included, and treat a 401 as
 * "not signed in." Account.jsx's own version redirects to /login,
 * but /login has no matching route in this app (it 404s) — this
 * gate redirects to /portal instead, the app's actual working
 * sign-in entry point (same target BundleDisclosure.jsx already
 * uses for the same "not signed in" case).
 *
 * While the check is in flight, renders nothing but a quiet loading
 * line — never the protected children — so a logged-out visitor
 * never sees a flash of gated content before the redirect fires.
 */
import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { BACKEND_URL } from "@/lib/backendUrl";

export default function RequireAuth({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [authorized, setAuthorized] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/auth/me`, {
          credentials: "include",
        });
        if (cancelled) return;
        if (!res.ok) {
          navigate(`/portal?next=${encodeURIComponent(location.pathname)}`, {
            replace: true,
          });
          return;
        }
        setAuthorized(true);
      } catch {
        if (!cancelled) {
          navigate(`/portal?next=${encodeURIComponent(location.pathname)}`, {
            replace: true,
          });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [navigate, location.pathname]);

  if (!authorized) {
    return (
      <div data-testid="require-auth-loading" className="aurin-section">
        <div className="aurin-container max-w-[700px] py-20">
          <p className="text-[14px] italic text-[hsl(var(--aurin-text-muted))]">
            Checking your session…
          </p>
        </div>
      </div>
    );
  }

  return children;
}
